import React, { useEffect, useMemo, useState } from "react";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "ui/Carousel";
import { Action } from "util/constants";
import api from "config/axiosConfig";
import { getUserRelationsByUserId, PrivacyLevel } from "util/userUtils";
import ActionCardRefactored from "components/Actions/ActionCardRefactored";
import { apiRoutes } from "util/apiRoutes";

interface UserActivityCarouselProps {
    userId: number;
}

interface ActivityRelation {
    id: number;
    user_id: number;
    rel_node_type: string;
    rel_node_id: number;
    rel_type: string;
    properties?: {
        privacy?: PrivacyLevel;
        [key: string]: unknown;
    };
    created_at: string;
    updated_at: string;
}

const UserActivityCarousel: React.FC<UserActivityCarouselProps> = ({ userId }) => {
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [actions, setActions] = useState<Action[]>([]);

    useEffect(() => {
        let isMounted = true;
        const load = async () => {
            setIsLoading(true);
            setError(null);
            try {
                const relationsRes = await getUserRelationsByUserId(userId, {
                    relTypes: ["activity"],
                    relNodeTypes: ["action"],
                    limit: 50,
                    sortOrder: "DESC"
                });

                const relationsRaw: unknown = relationsRes?.relations ?? [];
                const relations: ActivityRelation[] = Array.isArray(relationsRaw)
                    ? relationsRaw.filter((r): r is ActivityRelation =>
                        typeof (r as ActivityRelation).rel_node_id === "number" &&
                        (r as ActivityRelation).rel_node_type === "action" &&
                        (r as ActivityRelation).rel_type === "activity"
                    )
                    : [];

                if (relations.length === 0) {
                    if (isMounted) {
                        setActions([]);
                    }
                    return;
                }

                const actionFetches = relations.map(async (rel) => {
                    try {
                        const res = await api.get(apiRoutes.posts.byId(rel.rel_node_id));
                        return res.data as Action;
                    } catch {
                        return null;
                    }
                });

                const results = await Promise.all(actionFetches);
                const filtered = results.filter((m): m is Action => Boolean(m));
                if (isMounted) {
                    setActions(filtered);
                }
            } catch (e) {
                if (isMounted) {
                    setError("Failed to load activities");
                }
            } finally {
                if (isMounted) {
                    setIsLoading(false);
                }
            }
        };
        load();
        return () => { isMounted = false; };
    }, [userId]);

    const content = useMemo(() => {
        if (isLoading) {
            return (
                <div className="p-4 text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub">Loading...</div>
            );
        }
        if (error) {
            return (
                <div className="p-4 text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub">{error}</div>
            );
        }
        if (actions.length === 0) {
            return (
                <div className="p-4 text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub">No actions to display.</div>
            );
        }
        return (
            <Carousel className="user-activitea-carousel w-full">
                <CarouselContent>
                    {actions.map((m) => (
                        <CarouselItem key={m.id} className="basis-full sm:basis-1/1">
                            <div className="p-1">
                                <ActionCardRefactored action={m} isEntitySection={true} />
                            </div>
                        </CarouselItem>
                    ))}
                </CarouselContent>
                <div className="flex justify-between px-1">
                    <CarouselPrevious size="lg" />
                    <CarouselNext size="lg" />
                </div>
            </Carousel>
        );
    }, [isLoading, error, actions]);

    return (
        <div className="user-activitea-section w-full">
            {content}
        </div>
    );
};

export default UserActivityCarousel;


